import { defineComponent as _defineComponent } from 'vue'
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {debounce} from "@/utils/Helpers";
import IngredientDisplay from "../models/Ingredient/IngredientDisplay";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import VRecipeNutritionLabelPrintout from "@/components/vRecipeNutritionLabelPrintout.vue";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import {DishRecipeCommon} from "@/models/DishRecipeCommon";
import {LabelRenderDto} from "@/models/NutritionLabels/v2/RenderLabelDto";
import {useAccountState} from "@/hooks/useAccountState";
import {computed, onMounted, provide, Ref, ref} from "vue";
import {SaveCustomizedLabelDataDto} from "@/models/NutritionLabels/v2/SaveCustomizedLabelDataDto";
import LabelPreviewImage from "./LabelPreviewImage.vue";
import VBackOfPackageConfig from "./vBackOfPackageConfig.vue";
import ExportLabelButtons from "./ExportLabelButtons.vue";
import VFrontOfPackageConfig from "@/components/vFrontOfPackageConfig.vue";
import VCautionaryFOPConfig from "@/components/vCautionaryFOPConfig.vue";
import VCautionaryInputs from "@/components/vCautionaryInputs.vue";
import {CautionaryFoodType} from "@/models/Cautionary/CautionaryFoodType";
import {CaffeinatedProductType} from "@/models/Cautionary/CaffeinatedProductType";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {ExportLabelState2} from "@/models/NutritionLabels/v2/ExportLabelState2";
import {AxiosError} from "axios";
import {useNotify} from "@/hooks/useNotify";
import {useTranslations} from "@/hooks/useTranslations";
import RecipesRepository from "@/utils/API/RecipesRepository";

type ExportLabelProps = {
  recipe?: DishRecipeCommon;
  packageLabelType?: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts;
  subrecipeServingSizes: RecipeCompositionMeasurement[];
  printMeta: PrintRecipeDetails;
  preselectedLabelType: LabelTypeMetadata;
  highIn?: CanadaFOPHighInMetadata | CanadaFOPHighInMetadata.None;
  roundingRuleIndex: number;
  subrecipeMeasurements: RecipeCompositionMeasurement[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'vExportLabel',
  props: {
    recipe: null,
    packageLabelType: null,
    subrecipeServingSizes: { default: () => [] },
    printMeta: null,
    preselectedLabelType: { default: LabelTypeMetadata.NutritionFacts },
    highIn: null,
    roundingRuleIndex: { default: 0 },
    subrecipeMeasurements: { default: () => [] }
  },
  emits: ["update-metadata"],
  setup(__props: any, { emit }) {

const props = __props as { recipe?: DishRecipeCommon, packageLabelType?: LabelTypeMetadata | LabelTypeMetadata.NutritionFacts, subrecipeServingSizes: RecipeCompositionMeasurement[], printMeta: PrintRecipeDetails, preselectedLabelType: LabelTypeMetadata, highIn?: CanadaFOPHighInMetadata | CanadaFOPHighInMetadata.None, roundingRuleIndex: number, subrecipeMeasurements: RecipeCompositionMeasurement[] };





const notify = useNotify();
const accountState = useAccountState();
const translations = useTranslations();

const exportLabelState = ref<ExportLabelState2>({
  nutritionCountry: props.recipe.nutritionCountry,
  labelLanguage: LabelLanguage.English,
  labelPreview: true,
  disableDownload: false,
  hasValidCautionary: false,
  labelTypes: {
    [LabelTypeMetadata.CautionarySideOfPack]: {
      foodType: CautionaryFoodType.None,
      caffeinatedType: CaffeinatedProductType.None,
      quantityInPackage: 1,
      labelWidth: 1
    }
  },
});
const canPrint = ref(false);

const bopConfigRef = ref(null);
const fopConfigRef = ref(null);
const cfopConfigRef = ref(null);
const csopConfigRef = ref(null);
const buttonsRef = ref(null);

const fetchedLabel: Ref<SaveCustomizedLabelDataDto | null> = ref(null);
const labelPreviewBlob: Ref<Blob | null> = ref(null);
const activeView = ref(ExportLabelView.BOP);
const previewAbortController = ref(new AbortController());
const isSupplementLabel = ref(false);
const isLabelLoading = ref(false);
const labelLoadErrorTranslation = ref<string>("");
const ingredientAndSubRecipeDisplaysModel: Ref<{
  combinedSubRecipeList: IngredientDisplay[],
  expandedSubRecipeList: IngredientDisplay[]}
> = ref({ combinedSubRecipeList: [], expandedSubRecipeList: [] });
const savedLabelId = ref(null);
const isCautionaryEligible = ref(false);

const shouldDisplayLabel = computed(() => labelPreviewBlob.value !== null);

onMounted(async() => {
  await fetchSavedLabel();
});

function onCsopDataChanged(data: any) {
  exportLabelState.value.labelTypes = data;
}

function handleLanguageChange(language: LabelLanguage) {
  exportLabelState.value.labelLanguage = language;
}

/**
 * Fetches the saved label data for a specific recipe and initializes ingredient and sub-recipe displays.
 *
 * @return {Promise<void>} A promise that resolves when the label data is fetched and processed. If an error occurs during the fetch, it will be logged to the console.
 */
async function fetchSavedLabel() {
  try {
    const response = await NutritionLabelRepository.getLabelConfigurationV2(props.recipe.id);
    fetchedLabel.value = response.data;
    savedLabelId.value = fetchedLabel.value.id;
    bopConfigRef.value.savedLabelConfig = fetchedLabel.value;

    console.log("Fetched label:", fetchedLabel.value);
  }
  catch (e) {
    console.error(e);
  }
}

/**
 * Fetches a preview of the label based on the provided render data.
 * @param renderData
 * @param requestingScreen
 */
async function fetchPreview(renderData: LabelRenderDto, requestingScreen: ExportLabelView) {
  // Sanity check
  if (requestingScreen !== buttonsRef.value.viewManager.currentPageKey) {
    return;
  }

  isLabelLoading.value = true;
  canPrint.value = false;

  try {
    const response = await NutritionLabelRepository.downloadLabelV2([renderData]);

    if (response !== undefined) {
      labelPreviewBlob.value = response.data;

      labelLoadErrorTranslation.value = "";
      isLabelLoading.value = false;
      return response.data;
    }
  }

  catch (ex: AxiosError<{message: string}>) {
    isLabelLoading.value = false;

    if (ex.response.headers["content-type"].includes("application/json")) {
      const text = await ex.response.data.text();
      const json = JSON.parse(text);
      labelLoadErrorTranslation.value = json.message;
      return;
    }

    labelLoadErrorTranslation.value = ex.response?.data?.message || ex.message;
  }
}
provide("fetchPreview", fetchPreview);
provide("bopConfigRef", bopConfigRef);
provide("fopConfigRef", fopConfigRef);
provide("cfopConfigRef", cfopConfigRef);
provide("csopConfigRef", csopConfigRef);

function print() {
  window.print();
}

async function saveCustomizedLabel(saveData: SaveCustomizedLabelDataDto) {
  const response = await NutritionLabelRepository.saveLabelConfigurationV2(saveData);
  notify.success(translate("labels_save_success"));
  return response.data;
}

function handleCautionaryEligibility(isEligible: boolean) {
  isCautionaryEligible.value = isEligible;
}

function updateRecipeMetadata(isSupplement: boolean) {
  isSupplementLabel.value = isSupplement;
}

function translate(key: string) {
  return translations.value.get(key);
}

async function saveBopLabel() {
  if (!bopConfigRef.value) return;

  const config = bopConfigRef.value.renderModel;

  updateIsSupplementLabel();

  console.log("saveBopLabel - modelconfig:", bopConfigRef.value.model);
  console.log("saveBopLabel - renderModel:", config);

  // Build the DTO
  const saveData = {
    id: savedLabelId.value,
    recipeId: props.recipe.id,
    country: config.country,
    templateName: config.labelName,
    modelData: { ...config.renderModel }
  };

  console.log("saveBopLabel - saveData : ",saveData);

  savedLabelId.value = await saveCustomizedLabel(saveData);
}

function updateIsSupplementLabel(){
  // console.log("updateIsSupplementLabel - isSupplementLabel : ",isSupplementLabel.value);

  emit("update-metadata", isSupplementLabel.value);
  RecipesRepository.UpdateMetaDataIsSupplementLabel(props.recipe.id, isSupplementLabel.value);
}

/**
 * Debounces the fetch preview operation to prevent unnecessary rapid executions.
 * This method aborts any ongoing fetch preview operation before initiating a new one
 * with a delay to optimize API calls or computations.
 *
 * @return {void} Does not return a value.
 */
function debouncedFetchPreview(renderData: LabelRenderDto) {
  previewAbortController.value.abort("Refetch");
  debounce(() => fetchPreview(renderData), 1000, "export-label-fetch-preview");
}

/**
 * Handles the transition to a new view by setting the active view.
 *
 * @param {ExportLabelView} nextView - The view to switch to.
 * @return {void} This method does not return a value.
 */
function handleSwitchView(nextView: ExportLabelView) {
  activeView.value = nextView;
}

/**
 * Loads the FOP label creator by setting the active view to the FOP export label view.
 *
 * @return {void} This function does not return a value.
 */
function loadFOPLabelCreator() {
  activeView.value = ExportLabelView.FOP;
}

/**
 * Sets the active view to the BOP label export view.
 *
 * @return {void} Does not return a value.
 */
function loadBOPLabelCreator() {
  activeView.value = ExportLabelView.BOP;
}


return { __sfc: true,props, emit, notify, accountState, translations, exportLabelState, canPrint, bopConfigRef, fopConfigRef, cfopConfigRef, csopConfigRef, buttonsRef, fetchedLabel, labelPreviewBlob, activeView, previewAbortController, isSupplementLabel, isLabelLoading, labelLoadErrorTranslation, ingredientAndSubRecipeDisplaysModel, savedLabelId, isCautionaryEligible, shouldDisplayLabel, onCsopDataChanged, handleLanguageChange, fetchSavedLabel, fetchPreview, print, saveCustomizedLabel, handleCautionaryEligibility, updateRecipeMetadata, translate, saveBopLabel, updateIsSupplementLabel, debouncedFetchPreview, handleSwitchView, loadFOPLabelCreator, loadBOPLabelCreator, LabelTypeMetadata, ExportLabelView, VRecipeNutritionLabelPrintout, LabelPreviewImage, VBackOfPackageConfig, ExportLabelButtons, VFrontOfPackageConfig, VCautionaryFOPConfig, VCautionaryInputs, CollapsableCard }
}

})